import {
  DataModel, SerializationContext, SerializedData, DataModelBuilder, isSameList, isSameData,
} from '../../dto/dto.js';
import { Sector } from './sector.js';
import { SerialIdGenerator } from '../../core/serial-id-generator.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { SeatingElement } from './seating-element.js';
import { SeatRow } from './seat-row.js';
import { Seat } from './seat.js';
import { SeatingLabel } from './seating-label.js';
import { CameraConstraints } from './camera-constraints.js';
import { VersionRange } from '../../core/version-range.js';

/**
 * @deprecated Use `SeatingLayout' instead.
 */
export class LegacySeatingLayout extends DataModel {
  static readonly TYPE_ID: string = '9926bce12e62427abf828a720f9c22b1';

  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly backgroundImage: string | null,
    public readonly children: SeatingElement[],
    public readonly serialIdGenerator: SerialIdGenerator,
    public readonly cameraConstraints: CameraConstraints,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacySeatingLayout.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LegacySeatingLayout.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, data: SerializedData): LegacySeatingLayout {
    const decodeChild = (childData: SerializedData): SeatingElement => {
      const typeId = MapUtils.getNumber(childData, '$typeId');
      switch(typeId) {
        case Sector.TYPE_ID:
          return Sector.fromJson(childData);
        case SeatRow.TYPE_ID:
          return SeatRow.fromJson(childData);
        case Seat.TYPE_ID:
          return Seat.fromJson(childData);
        case SeatingLabel.TYPE_ID:
          return SeatingLabel.fromJson(childData);
      }

      throw Error(`Unknown SeatingElement of type: ${typeId}`);
    };

    return new LegacySeatingLayout(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        MapUtils.get<SerializedData[]>(data, 'children')
            .map((childData) => decodeChild(childData)),
        new SerialIdGenerator(MapUtils.get<number>(data, 'nextSerial')),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      name: this.name,
      backgroundImage: this.backgroundImage,
      children: this.children.map((child) => child.toJson()),
      nextSerial: this.serialIdGenerator.nextValue,
      cameraConstraints: this.cameraConstraints.toJson(),
    };
  }

  clone(): this {
    return new LegacySeatingLayout(
        this.uniqueId,
        this.name,
        this.backgroundImage,
        this.children.map((e) => e.clone(null)),
        this.serialIdGenerator.clone(),
        this.cameraConstraints.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.name === other.name &&
        this.backgroundImage === other.backgroundImage &&
        isSameList(this.children, other.children, (a, b) => isSameData(a, b)) &&
        isSameData(this.serialIdGenerator, other.serialIdGenerator) &&
        isSameData(this.cameraConstraints, other.cameraConstraints);
  }
}

export class LegacySeatingLayoutBuilder extends DataModelBuilder<LegacySeatingLayout> {
  build(context: SerializationContext, map: SerializedData): LegacySeatingLayout {
    return LegacySeatingLayout.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(
      context: SerializationContext,
      map: SerializedData,
  ): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacySeatingLayout.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacySeatingLayout.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *   - Add `cameraConstraints` of type {@link CameraConstraints}.
     */
    if(version < 2) {
      map['cameraConstraints'] = CameraConstraints.DEFAULT_CAMERA_CONSTRAINTS.toJson();
      version = 2;
    }

    /**
     * Version 3:
     *   - Add `backgroundImage` of type `string`.
     */
    if(version < 3) {
      map['backgroundImage'] = null;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
